import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { appConfig } from "../config/appConfig";
import { api, preloadRoundArt } from "../services";
import KeepOneArena from "./KeepOneArena";
import Wordmark from "./Wordmark";

// Orchestrates the serial KEEP ONE run against the live evidence API.
// Adaptive pairs are served incrementally: on each tap we record the choice and
// prefetch the next pair, gating the transition on the (fast) response AND the
// restrained animation delay so the rhythm stays smooth.

export default function PlayScreen({ runId, firstComparison, total, variant, reducedMotion, onComplete }) {
  const [comparison, setComparison] = useState(firstComparison);
  const [chosenSide, setChosenSide] = useState(null);
  const lockRef = useRef(false);
  const shownAtRef = useRef(Date.now());

  const round = { left: comparison.left, right: comparison.right };
  const sequence = comparison.sequence;

  useEffect(() => {
    shownAtRef.current = Date.now();
    api.logEvent("comparison_view", runId, {
      sequence: comparison.sequence,
      comparison_id: comparison.comparison_id,
    });
  }, [comparison.comparison_id, comparison.sequence, runId]);

  const resolve = useCallback(
    (side, unknown = false) => {
      if (lockRef.current) return;
      lockRef.current = true;

      const chosen = unknown ? null : side === "left" ? comparison.left : comparison.right;
      setChosenSide(unknown ? "unknown" : side);

      const body = {
        comparison_id: comparison.comparison_id,
        selected_id: chosen ? chosen.id : null,
        unknown,
        decided_ms: Date.now() - shownAtRef.current,
      };
      const req = api.submitChoice(runId, body);
      const delay = reducedMotion ? 130 : appConfig.motion.advanceDelayMs;
      const wait = new Promise((r) => setTimeout(r, delay));

      // Warm the next matchup's posters as soon as the server names them —
      // in parallel with the advance animation, before the swap renders.
      req.then((res) => { if (res && !res.done) preloadRoundArt(res.next); }).catch(() => {});

      Promise.all([req, wait])
        .then(([res]) => {
          if (res.done) {
            onComplete();
          } else {
            setComparison(res.next);
            setChosenSide(null);
            lockRef.current = false;
          }
        })
        .catch(() => {
          lockRef.current = false;
          setChosenSide(null);
          toast.error("That choice didn't save", { description: "Tap again to continue." });
        });
    },
    [comparison, runId, reducedMotion, onComplete]
  );

  const progress = String(sequence).padStart(2, "0");
  const totalStr = String(total).padStart(2, "0");

  return (
    <div className="relative flex flex-col h-[100dvh] w-full max-w-[520px] mx-auto tt-vignette">
      <header className="flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+16px)]">
        <Wordmark variant="horizontal" className="h-3.5 sm:h-4 opacity-90" />
        <span
          data-testid="round-progress"
          className="font-mono font-semibold tracking-[0.15em] text-xs text-subtle"
        >
          {progress} <span className="text-white/20">/</span> {totalStr}
        </span>
      </header>

      <div className="px-5 pt-3 pb-2 text-center">
        <h1 className="font-display font-black tracking-[-0.03em] text-2xl sm:text-3xl text-bone">
          KEEP ONE.
        </h1>
      </div>

      <main className="relative flex-1 min-h-0 px-4 pb-2 overflow-hidden">
        {/* mode="wait" held the outgoing round's DOM (its last props: stale
            title, chosenSide still set -> buttons stuck disabled) on screen
            until its exit transition reported completion. When that
            callback doesn't fire, the round freezes permanently even though
            comparison/chosenSide state has already advanced correctly. The
            default (overlapping) mode animates the new round in without
            waiting on the old one's exit, so state and DOM can never
            diverge like that.

            That overlap means the outgoing AND incoming round briefly
            coexist in the DOM. Both are `absolute inset-0` (not normal-flow
            siblings) so they're overlaid on top of one another within this
            `relative overflow-hidden` box instead of stacking and doubling
            main's content height — which used to push the incoming round's
            real poster UI (not just its preloaded images) into view for a
            frame before the outgoing round finished unmounting. */}
        <AnimatePresence>
          <motion.div
            key={comparison.comparison_id}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 14, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.99 }}
            transition={{ duration: reducedMotion ? 0.12 : 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 h-full w-full"
          >
            <KeepOneArena
              round={round}
              variant={variant}
              chosenSide={chosenSide}
              onChoose={(side) => resolve(side)}
              reducedMotion={reducedMotion}
            />
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="px-5 pt-1 pb-[calc(env(safe-area-inset-bottom)+18px)] flex justify-center">
        <button
          type="button"
          data-testid="havent-seen-btn"
          disabled={!!chosenSide}
          onClick={() => resolve(null, true)}
          className="font-body font-medium text-[11px] tracking-[0.18em] uppercase text-white/45 hover:text-white/80 disabled:opacity-40 transition-colors px-4 py-2 rounded-full border border-white/10 hover:border-white/20 bg-white/[0.03]"
        >
          Haven't seen / played
        </button>
      </footer>
    </div>
  );
}
